import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Divider,
    FormControl,
    FormHelperText,
    GridLegacy as Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { themeColors } from '@styles/theme';
import { CircledLockIcon } from '@components/icons/icons/CircledLockIcon';
import './new-password-page.scss';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { AuthPagesWrapper } from '@components/AuthPagesWrapper/AuthPagesWrapper';
import PasswordResetFinishedPage from '../ResetPasswordFinishedPage/PasswordResetFinishedPage';
import BackToSignInButton from '@components/BackToSignInButton/BackToSignInButton';
import { useAuth } from '@context/authContext';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { useToastContext } from '@context/toastContext';

type FormValuesType = {
    password: string;
    passwordRepeat: string;
};

export const NewPasswordPage = () => {
    const [showPassword, setPasswordVisibility] = useState<boolean>(false);
    const [showPasswordRepeat, setPasswordRepeatVisibility] = useState<boolean>(false);
    const [passwordUpdated, setPasswordUpdated] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        handleSubmit,
        control,
        getValues,
        formState: { errors },
    } = useForm<FormValuesType>();
    const { changeUserPassword } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { addToast } = useToastContext();

    const login = searchParams.get('user');
    const token = searchParams.get('token');

    useEffect(() => {
        if (!login || !token) {
            navigate('/signing', { state: { from: location.pathname } });
        }
    }, [login, token]);

    const validatePassword = useCallback(
        (password: string, login: string | null): boolean | string => {
            if (!login || !token) {
                return t('new-password-page.validation-errors.invalid-link');
            }

            if (!password || password.trim().length === 0) {
                return t('new-password-page.validation-errors.empty-password');
            }

            if (password.length < 8 || password.length > 60) {
                return t('new-password-page.validation-errors.password-length');
            }

            if (password !== password.trim()) {
                return t('new-password-page.validation-errors.starts-from-space');
            }

            if (/^(.)\1+$/.test(password)) {
                return t('new-password-page.validation-errors.repeating-symbol');
            }

            const uniqueChars = new Set(password).size;
            if (uniqueChars < 3) {
                return t('new-password-page.validation-errors.min-three-unique-chars');
            }

            const commonSequences = [
                '12345678',
                '87654321',
                '123456789',
                '987654321',
                'abcdefgh',
                'hgfedcba',
                'qwertyui',
                'asdfghjk',
                'qwerty',
                'password',
                '00000000',
                '11111111',
            ];
            const lowerPassword = password.toLowerCase();
            if (commonSequences.some(seq => lowerPassword.includes(seq))) {
                return t('new-password-page.validation-errors.common-sequences');
            }

            if (login && password.toLowerCase().includes(login.toLowerCase())) {
                return t('new-password-page.validation-errors.password-contains-login');
            }

            return true;
        },
        [login, token]
    );

    const onSubmit: SubmitHandler<FormValuesType> = useCallback(
        async ({ password, passwordRepeat }) => {
            if (login && password && passwordRepeat && token) {
                const resp = await changeUserPassword({ login, password, passwordRepeat, token });
                if (resp && isErrorResponse(resp)) {
                    try {
                        const parsedErr =
                            typeof resp.error === 'string'
                                ? safelyParseJson(resp.error)?.error
                                : resp.error.error;
                        addToast(parsedErr, 'error');
                    } catch (e) {
                        console.log('Replan. Error parsing error response', resp, e);
                    }
                } else {
                    setPasswordUpdated(true);
                }
            }
        },
        [login, token, changeUserPassword]
    );

    const handleClickShowPassword = () => {
        setPasswordVisibility(prevState => !prevState);
    };

    const handleClickShowPasswordRepeat = () => {
        setPasswordRepeatVisibility(prevState => !prevState);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    if (passwordUpdated) {
        return <PasswordResetFinishedPage />;
    }

    return (
        <AuthPagesWrapper showLogoColumn={false}>
            <div className="new-password-page__row new-password-page__icon">
                <CircledLockIcon />
            </div>
            <h1 className="new-password-page__row new-password-page__title">
                {t('new-password-page.title')}
            </h1>
            <p className="new-password-page__row new-password-page__description">
                {t('new-password-page.description')}
            </p>
            <Grid item xs={12} sm={12}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Controller
                        name="password"
                        control={control}
                        rules={{
                            required: true,
                            validate: value => validatePassword(value, login),
                        }}
                        render={({ field, fieldState, formState }) => (
                            <FormControl sx={{ my: 1, width: '100%' }} variant="outlined" required>
                                <InputLabel htmlFor="password">
                                    {t('new-password-page.password')}
                                </InputLabel>
                                <OutlinedInput
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!errors.password}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label={t('new-password-page.password')}
                                    sx={{ background: themeColors.white }}
                                    required
                                />
                                {!!errors.password && (
                                    <FormHelperText error id="password-error">
                                        {errors.password.message ||
                                            t('new-password-page.password-error')}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        )}
                    />
                    <Controller
                        name="passwordRepeat"
                        control={control}
                        rules={{
                            required: true,
                            validate: value =>
                                value === getValues('password') ||
                                t('new-password-page.match-error'),
                        }}
                        render={({ field, fieldState, formState }) => (
                            <FormControl sx={{ my: 1, width: '100%' }} variant="outlined" required>
                                <InputLabel htmlFor="repeat-password">
                                    {t('new-password-page.repeat-password')}
                                </InputLabel>
                                <OutlinedInput
                                    id="repeat-password"
                                    type={showPasswordRepeat ? 'text' : 'password'}
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    error={!!errors.passwordRepeat}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleClickShowPasswordRepeat}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPasswordRepeat ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label={t('new-password-page.repeat-password')}
                                    sx={{ background: themeColors.white }}
                                    required
                                />
                                {!!errors.passwordRepeat && (
                                    <FormHelperText error id="accountId-error">
                                        {errors.passwordRepeat.message ||
                                            t('new-password-page.match-error')}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        )}
                    />
                    <Divider sx={{ mt: 4 }} />
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ my: 2 }}
                    >
                        <BackToSignInButton title={t('reset-password-form.back-to-login-link')} />
                        <Button
                            type="submit"
                            variant="contained"
                            style={{ background: themeColors.purple }}
                        >
                            {t('new-password-page.button')}
                        </Button>
                    </Grid>
                </form>
            </Grid>
        </AuthPagesWrapper>
    );
};
